'use client'

import Link from 'next/link'
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react'
import { platformFetch } from '@/lib/platform/client-headers'

/**
 * The /platform/sign-in gate: the reference markup
 * (pixelmojo-rebuild/pixelmojo-platform-sign-in.html) as a component instead
 * of the reference's inline script (Qodo, PR #390). The idle and sent views
 * keep the reference classes, so platform-sign-in.css styles them unchanged.
 * Over the script it adds what the pre-overhaul page had:
 * - a visitor who already has a session gets account, audit and sign-out
 *   actions instead of a form that would mail them a link they don't need;
 * - one link request at a time: the send button is disabled and a second
 *   submit is ignored while a request is in flight, since send-link allows
 *   five requests a window;
 * and ties the error to the input (aria-invalid, aria-describedby) inside an
 * alert region, so it is announced.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const RESEND_AFTER = 30

type View = 'idle' | 'sent' | 'signed-in'

async function requestLink(email: string) {
  const res = await platformFetch('/api/platform/auth/send-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.message || 'Failed to send link')
}

/** A rejected fetch says "Failed to fetch"; visitors get the old wording. */
function errorText(err: unknown) {
  if (err instanceof TypeError || !(err instanceof Error) || !err.message) {
    return 'Network error. Try again.'
  }
  return err.message
}

const arrow = (
  <svg
    className='arrow'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
  >
    <path d='M5 12h14M13 6l6 6-6 6' />
  </svg>
)

export default function SignInGate() {
  const [view, setView] = useState<View>('idle')
  const [account, setAccount] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [resending, setResending] = useState(false)
  const [countdown, setCountdown] = useState(RESEND_AFTER)
  const inFlight = useRef(false)
  const input = useRef<HTMLInputElement>(null)

  // The session check the pre-overhaul page ran, the way SignInLink runs it
  // (platformFetch, so token-only sessions count). Silent on failure: the
  // form stays.
  useEffect(() => {
    let cancelled = false
    platformFetch('/api/platform/tier')
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d?.email) return
        setAccount(String(d.email))
        setView('signed-in')
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (view !== 'sent' || countdown <= 0) return
    const tick = window.setTimeout(() => setCountdown(n => n - 1), 1000)
    return () => window.clearTimeout(tick)
  }, [view, countdown])

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (inFlight.current) return
    const value = email.trim()
    if (!EMAIL.test(value)) {
      setError('Enter a valid email address.')
      input.current?.focus()
      return
    }
    setError(null)
    inFlight.current = true
    setBusy(true)
    try {
      await requestLink(value)
      setSentTo(value)
      setCountdown(RESEND_AFTER)
      setView('sent')
    } catch (err) {
      setError(errorText(err))
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  async function resend() {
    if (inFlight.current) return
    inFlight.current = true
    setResending(true)
    try {
      await requestLink(sentTo)
      setCountdown(RESEND_AFTER)
    } catch {
      // The button comes back as "Resend link", as the reference's did.
      setCountdown(0)
    } finally {
      inFlight.current = false
      setResending(false)
    }
  }

  function pickOtherEmail() {
    setView('idle')
    setEmail('')
    setError(null)
    window.requestAnimationFrame(() => input.current?.focus())
  }

  async function signOut() {
    await fetch('/api/platform/auth/sign-out', { method: 'POST' })
    window.location.reload()
  }

  return (
    <figure
      className='gate'
      id='gate'
      data-state={view}
      data-reveal='fade'
      aria-label='Sign in form'
    >
      <div className='gate__idx'>
        <span>
          <b>{'Account access'}</b>
        </span>
        <span className='ok'>
          {view === 'signed-in' ? 'Signed in' : 'Magic link'}
        </span>
      </div>{' '}
      {view === 'signed-in' ? (
        <div className='gate__body'>
          <h2>{"You're signed in"}</h2>{' '}
          <p>
            {'Signed in as '}
            <b
              style={
                { color: 'var(--paper)', fontWeight: '600' } as CSSProperties
              }
            >
              {account}
            </b>
          </p>{' '}
          <div className='gate__form'>
            <Link className='btn btn--accent' href='/platform/account'>
              {'Go to Account '}
              {arrow}
            </Link>
          </div>{' '}
          <div className='gate__alt'>
            <Link
              href='/platform/app'
              target='_blank'
              rel='noopener noreferrer'
            >
              {'Run an audit'}
            </Link>
            <button type='button' onClick={signOut}>
              {'Not you? Sign out'}
            </button>
          </div>
        </div>
      ) : (
        <div className='gate__body'>
          <h2>{'Sign in to your account'}</h2>{' '}
          <p>
            {
              "Enter your email. We'll send you a sign-in link. No password required."
            }
          </p>{' '}
          <form
            className={`gate__form${error ? ' is-invalid' : ''}`}
            id='signin'
            action='#'
            method='post'
            noValidate
            onSubmit={submit}
          >
            <label htmlFor='email'>{'Work email'}</label>{' '}
            <div className='field'>
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.8'
              >
                <rect x='3' y='5' width='18' height='14' rx='2' />
                <path d='m3 7 9 6 9-6' />
              </svg>
              <input
                ref={input}
                id='email'
                name='email'
                type='email'
                inputMode='email'
                autoComplete='email'
                placeholder='you@company.com'
                required
                value={email}
                onChange={e => {
                  setEmail(e.target.value)
                  if (error) setError(null)
                }}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'err' : undefined}
              />
            </div>{' '}
            <span className='gate__err' id='err' role='alert'>
              {error ?? ''}
            </span>{' '}
            <button
              className={`btn btn--accent${busy ? ' is-busy' : ''}`}
              type='submit'
              id='send'
              disabled={busy}
            >
              <span className='spin' aria-hidden='true' />
              {'Send sign-in link '}
              {arrow}
            </button>{' '}
          </form>{' '}
          <div className='gate__alt'>
            <span>
              {"Don't have an account? "}
              <Link href='/pricing'>{'See pricing'}</Link>
            </span>
            <Link href='/platform#free-audit'>
              {'Run the free check first'}
            </Link>
          </div>{' '}
          <div className='gate__sent' aria-live='polite'>
            <div className='mail'>
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M20 6 9 17l-5-5' />
              </svg>
              <div>
                <b id='sentTo'>{sentTo || 'you@company.com'}</b>
                <span>{'Sign-in link sent. It expires in 15 minutes.'}</span>
              </div>
            </div>{' '}
            <ol className='steps'>
              <li>
                <span>{'1'}</span>
                {'Open the email from Radar by Pixelmojo.'}
              </li>
              <li>
                <span>{'2'}</span>
                {'Click '}
                <b
                  style={
                    {
                      color: 'var(--paper)',
                      fontWeight: '600',
                    } as CSSProperties
                  }
                >
                  {'Sign in to Radar'}
                </b>
                {' on the same device.'}
              </li>
              <li>
                <span>{'3'}</span>
                {
                  'You land on your account with audits, credits, and watched domains.'
                }
              </li>
            </ol>{' '}
            <div className='row'>
              <button
                type='button'
                id='resend'
                disabled={resending || countdown > 0}
                onClick={resend}
              >
                {resending ? (
                  'Sending…'
                ) : countdown > 0 ? (
                  <>
                    {'Resend in '}
                    <span id='cd'>{countdown}</span>
                    {'s'}
                  </>
                ) : (
                  'Resend link'
                )}
              </button>
              <button type='button' id='other' onClick={pickOtherEmail}>
                {'Use a different email'}
              </button>
            </div>{' '}
          </div>{' '}
        </div>
      )}{' '}
      <figcaption className='gate__cap'>
        <span>
          <b>{'Privacy'}</b>
          {' · we only use your email to sign you in'}
        </span>
        <Link
          href='/privacy-policy'
          style={{ color: 'var(--paper)' } as CSSProperties}
        >
          {'Privacy policy'}
        </Link>
      </figcaption>
    </figure>
  )
}
